import React, { useEffect, useState } from "react";
import { Search, Edit, MoreHorizontal, Image, Package, Wand2, Filter, SortAsc, SortDesc } from "lucide-react";
import { getAuthData, getStoreId } from "../utils/auth";

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [generatingAltText, setGeneratingAltText] = useState({});
  const [altTextFilter, setAltTextFilter] = useState("missing"); // "all", "missing", "with" - default to "missing"
  const [stats, setStats] = useState({ totalProducts: 0, totalImages: 0, loading: true });

  const itemsPerPageOptions = [10, 20, 50, 100];

  useEffect(() => {
    fetchProducts();
  }, [currentPage, itemsPerPage, selectedCategory, searchTerm, sortField, sortDirection, altTextFilter]);

  useEffect(() => {
    fetchCategories();
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const authData = getAuthData();
      if (!authData?.store_id || !authData?.access_token) {
        return;
      }

      const apiUrl = import.meta.env.VITE_BACKEND_URL;
      const response = await fetch(
        `${apiUrl}/api/products/stats?storeId=${authData.store_id}&token=${authData.access_token}`
      );
      const data = await response.json();

      if (response.ok && data.success) {
        setStats({
          totalProducts: data.totalProducts || 0,
          totalImages: data.totalImages || 0,
          loading: false
        });
      } else {
        setStats({ totalProducts: 0, totalImages: 0, loading: false });
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
      setStats({ totalProducts: 0, totalImages: 0, loading: false });
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId !== null) {
        setOpenDropdownId(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openDropdownId]);

  // Success and error message auto-hide
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      const authData = getAuthData();

      if (!authData?.store_id || !authData?.access_token) {
        console.error("Not authenticated");
        return;
      }

      const apiUrl = import.meta.env.VITE_BACKEND_URL;
      const allCategories = [];
      let offset = 0;
      const limit = 100; // Fetch in batches of 100
      let total = null;
      let hasMore = true;

      // Fetch all categories in batches
      while (hasMore) {
        const response = await fetch(
          `${apiUrl}/api/categories?storeId=${authData.store_id}&token=${authData.access_token}&limit=${limit}&offset=${offset}`
        );
        const data = await response.json();

        if (response.ok) {
          // Get total count from first response
          if (total === null) {
            total = data.total || 0;
          }
          
          if (data.items && data.items.length > 0) {
            allCategories.push(...data.items);
            offset += limit;
            // Continue if we got a full batch and haven't reached the total
            hasMore = data.items.length === limit && allCategories.length < total;
          } else {
            hasMore = false;
          }
        } else {
          console.error("Failed to fetch categories:", data.error);
          hasMore = false;
        }
      }

      setCategories(allCategories);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const authData = getAuthData();
      const storeId = getStoreId();

      if (!authData?.store_id || !authData?.access_token) {
        console.error("Not authenticated");
        setLoading(false);
        return;
      }

      const apiUrl = import.meta.env.VITE_BACKEND_URL;
      const settingsResponse = await fetch(`${apiUrl}/api/settings?id=${storeId}`);
      if (settingsResponse.ok) {
        const settingsData = await settingsResponse.json();
        if (settingsData.success && settingsData.settings) {
          setSettings(settingsData.settings);
        }
      }

      const offset = (currentPage - 1) * itemsPerPage;
      let url = `${apiUrl}/api/products?storeId=${authData.store_id}&token=${authData.access_token}&limit=${itemsPerPage}&offset=${offset}`;

      // Add category filter if specified
      if (selectedCategory) {
        url += `&category=${selectedCategory}`;
      }

      // Add search keyword if specified
      if (searchTerm) {
        url += `&keyword=${encodeURIComponent(searchTerm)}`;
      }

      // Add sorting if specified
      if (sortField) {
        url += `&sort=${sortField}&direction=${sortDirection}`;
      }

      // Add alt text filter if specified
      if (altTextFilter && altTextFilter !== "all") {
        url += `&altTextFilter=${encodeURIComponent(altTextFilter)}`;
      }

      const response = await fetch(url);
      const data = await response.json();

      if (response.ok) {
        setProducts(data.items || []);
        setTotalProducts(data.total || 0);
      } else {
        console.error("Failed to fetch products:", data.error);
      }
    } catch (error) {
      console.error("Failed to fetch products:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when search changes
  };

  // Products are now sorted server-side via API, so we use them directly
  const sortedProducts = products;

  const onItemsPerPageChange = (newRange) => {
    setCurrentPage(1);
    setItemsPerPage(newRange);
  };

  const onSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page when sort changes
  };

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const clearFilters = () => {
    setSelectedCategory("");
    setSearchTerm("");
    setSortField("");
    setSortDirection("asc");
    setAltTextFilter("all");
    setCurrentPage(1);
  };

  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      }
      return [...prev, productId];
    });
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === sortedProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(sortedProducts.map(product => product.id));
    }
  };

  const handleBulkAltText = async () => {
    if (selectedProducts.length > 0) {
      // Check if credits are available
      if (!settings || settings.credits_available <= 0) {
        setErrorMessage("You are out of credits. Please purchase more credits to continue generating alt text.");
        return;
      }

      try {
        const authData = getAuthData();
        const storeId = getStoreId();

        if (!authData?.store_id || !authData?.access_token) {
          console.error("Not authenticated");
          return;
        }

        const selectedProductObjects = sortedProducts.filter(product =>
          selectedProducts.includes(product.id)
        ).map(product => ({
          id: product.id,
          name: product.name,
          brand_id: product.brandId || 0
        }));

        console.log("selectedProductObjects", selectedProductObjects);

        const apiUrl = import.meta.env.VITE_BACKEND_URL;
        const response = await fetch(`${apiUrl}/api/bulkproduct`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            storeId: storeId,
            selectedProductObjects: selectedProductObjects
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to generate alt text');
        }

        setSuccessMessage(`Bulk alt text generation started for ${selectedProducts.length} products. Please check back in a few minutes.`);
        setSelectedProducts([]);
      } catch (error) {
        console.error("Failed to generate bulk alt text:", error);
        setErrorMessage(error.message);
      }
    }
  };

  const handleGenerateAltText = async (productId, productName) => {
    // Check if credits are available
    if (!settings || settings.credits_available <= 0) {
      setErrorMessage("You are out of credits. Please purchase more credits to continue generating alt text.");
      return;
    }

    setGeneratingAltText(prev => ({ ...prev, [productId]: true }));

    try {
      const authData = getAuthData();
      const storeId = getStoreId();

      if (!authData?.store_id || !authData?.access_token) {
        console.error("Not authenticated");
        return;
      }

      const selectedProductObjects = [{
        id: productId,
        name: productName,
        brand_id: 0 // Default brand ID for Ecwid
      }];

      const apiUrl = import.meta.env.VITE_BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/bulkproduct`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          storeId: storeId,
          selectedProductObjects: selectedProductObjects
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate alt text');
      }

      setSuccessMessage(`Alt text generation started for "${productName}". Please check back in a few minutes.`);
      setOpenDropdownId(null);
    } catch (error) {
      console.error("Failed to generate alt text:", error);
      setErrorMessage(error.message);
    } finally {
      setGeneratingAltText(prev => ({ ...prev, [productId]: false }));
    }
  };



  const toggleDropdown = (id, event) => {
    event.stopPropagation();
    setOpenDropdownId(openDropdownId === id ? null : id);
  };

  const handleEditProduct = (productId) => {
    // Open Ecwid product editor
    const authData = getAuthData();
    if (authData?.store_id) {
      const productUrl = `https://my.ecwid.com/store/${authData.store_id}#product:mode=edit&id=${productId}&return=products`;
      window.open(productUrl, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="text-center">
          <div className="loading-dots mb-3" role="status" style={{ width: '24px', height: '24px', margin: '0 auto' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted" style={{ fontSize: '0.875rem' }}>Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start mb-4">
                <div>
                  <h3 className="mb-2 fw-bold" style={{ fontSize: '1.25rem' }}>My Products</h3>
                  <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                    View and manage your products with ease. This section allows you to browse through your product catalog, and generate alt text for images.
                  </p>
                </div>
                {/* Stats Widget */}
                <div className="d-flex gap-3">
                  <div className="text-end">
                    <div className="fw-bold" style={{ fontSize: '1.5rem', color: '#2563eb' }}>
                      {stats.loading ? '...' : stats.totalProducts.toLocaleString()}
                    </div>
                    <div className="text-muted d-flex align-items-center justify-content-end gap-1" style={{ fontSize: '0.75rem' }}>
                      <Package size={14} />
                      <span>Total Products</span>
                    </div>
                  </div>
                  <div className="text-end">
                    <div className="fw-bold" style={{ fontSize: '1.5rem', color: '#10b981' }}>
                      {stats.loading ? '...' : `${((stats.totalProducts || 0) * 4).toLocaleString()}`}
                    </div>
                    <div className="text-muted d-flex align-items-center justify-content-end gap-1" style={{ fontSize: '0.75rem' }}>
                      <Image size={14} />
                      <span>Total Alt Texts</span>
                    </div>
                  </div>
                </div>
              </div>

              {successMessage && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                  {successMessage}
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setSuccessMessage(null)}></button>
                </div>
              )}

              {errorMessage && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  {errorMessage}
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setErrorMessage(null)}></button>
                </div>
              )}
              {/* Tip for bulk selection */}
              {sortedProducts.length > 0 && selectedProducts.length === 0 && (
                <div className="alert alert-info" role="alert" style={{ fontSize: '0.875rem' }}>
                  Tip: Select multiple products to enable bulk alt text generation.
                </div>
              )}

              {settings && settings.credits_available <= 0 && (
                <div className="alert alert-danger d-flex align-items-center gap-2 mb-3" role="alert" style={{ fontSize: '0.875rem' }}>
                  <span className="fw-bold me-2">Warning:</span>
                  You are out of credits. Please <a href="https://alttextify.net/plans" className="ms-1">purchase more credits</a> to continue generating alt text.
                </div>
              )}

              {/* Search, Filters and Bulk Actions */}
              <div className="row mb-3">
                <div className="col-md-9">
                  <div className="row g-2">
                    <div className="col-md-3">
                      <div className="input-group">
                        <span className="input-group-text">
                          <Search size={20} />
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search products..."
                          value={searchTerm}
                          onChange={handleSearch}
                        />
                      </div>
                    </div>
                    <div className="col-md-2">
                      <select
                        className="form-select"
                        value={selectedCategory}
                        onChange={(e) => handleCategoryChange(e.target.value)}
                        disabled={categoriesLoading}
                      >
                        <option value="">All Categories</option>
                        {categoriesLoading ? (
                          <option disabled>Loading categories...</option>
                        ) : (
                          categories.map(category => (
                            <option key={category.id} value={category.id}>
                              {category.name} ({category.productCount || 0})
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                    <div className="col-md-3">
                      <div className="input-group">
                            <span className="input-group-text">
                              <Filter size={16} />
                            </span>
                            <select
                              className="form-select"
                              value={altTextFilter}
                              onChange={(e) => {
                                setAltTextFilter(e.target.value);
                                setCurrentPage(1);
                              }}
                            >
                              <option value="all">All Products</option>
                              <option value="missing">Missing Alt Text</option>
                              <option value="with">With Alt Text</option>
                            </select>
                          </div>
                    </div>
                    <div className="col-md-2">
                      <select
                        className="form-select"
                        value={sortField}
                        onChange={(e) => onSort(e.target.value)}
                      >
                        <option value="">Sort by...</option>
                        <option value="name">Name</option>
                        <option value="price">Price</option>
                        <option value="created">Date Created</option>
                        <option value="updated">Last Updated</option>
                      </select>
                    </div>
                    <div className="col-md-1">
                      <div className="d-flex gap-1">
                        <button
                          className={`btn btn-sm ${sortDirection === 'asc' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setSortDirection('asc')}
                          disabled={!sortField}
                          title="Ascending"
                        >
                          <SortAsc size={14} />
                        </button>
                        <button
                          className={`btn btn-sm ${sortDirection === 'desc' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setSortDirection('desc')}
                          disabled={!sortField}
                          title="Descending"
                        >
                          <SortDesc size={14} />
                        </button>
                        {(selectedCategory || searchTerm || sortField || altTextFilter !== "all") && (
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={clearFilters}
                            title="Clear all filters"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3 text-end">
                  {selectedProducts.length > 0 && (
                    <div className="d-flex align-items-center gap-2 justify-content-end">
                      <button
                        className="btn btn-primary"
                        onClick={handleBulkAltText}
                      >
                        <img src="shortlogo.png" alt="AltTextify Logo" style={{ width: 20, height: 20, marginRight: 8 }} />
                        Bulk Generate Alt Text ({selectedProducts.length})
                      </button>
                      {/* <span className="text-muted" style={{ fontSize: '0.875rem' }}>{selectedProducts.length} products selected</span> */}
                    </div>
                  )}
                </div>
              </div>

              {/* Filter Summary */}
              {(selectedCategory || searchTerm || sortField || altTextFilter !== "all") && (
                <div className="mb-3">
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>Active filters:</small>
                    {selectedCategory && (
                      <span className="badge bg-primary">
                        Category: {categories.find(c => c.id == selectedCategory)?.name || selectedCategory}
                        <button
                          type="button"
                          className="btn-close btn-close-white ms-2"
                          style={{ fontSize: '0.5rem' }}
                          onClick={() => setSelectedCategory("")}
                        ></button>
                      </span>
                    )}
                    {altTextFilter === "missing" && (
                      <span className="badge bg-warning text-dark">
                        Missing Alt Text
                        <button
                          type="button"
                          className="btn-close btn-close-white ms-2"
                          style={{ fontSize: '0.5rem' }}
                          onClick={() => setAltTextFilter("all")}
                        ></button>
                      </span>
                    )}
                    {altTextFilter === "with" && (
                      <span className="badge bg-success">
                        With Alt Text
                        <button
                          type="button"
                          className="btn-close btn-close-white ms-2"
                          style={{ fontSize: '0.5rem' }}
                          onClick={() => setAltTextFilter("all")}
                        ></button>
                      </span>
                    )}
                    {searchTerm && (
                      <span className="badge bg-info">
                        Search: "{searchTerm}"
                        <button
                          type="button"
                          className="btn-close btn-close-white ms-2"
                          style={{ fontSize: '0.5rem' }}
                          onClick={() => setSearchTerm("")}
                        ></button>
                      </span>
                    )}
                    {sortField && (
                      <span className="badge bg-secondary">
                        Sort: {sortField} ({sortDirection})
                        <button
                          type="button"
                          className="btn-close btn-close-white ms-2"
                          style={{ fontSize: '0.5rem' }}
                          onClick={() => { setSortField(""); setSortDirection("asc"); }}
                        ></button>
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Products Table */}
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selectedProducts.length === sortedProducts.length && sortedProducts.length > 0}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th
                        style={{ width: '45%', cursor: 'pointer' }}
                        onClick={() => onSort('name')}
                        className={sortField === 'name' ? 'table-active' : ''}
                      >
                        <div className="d-flex align-items-center">
                          Product name
                          {sortField === 'name' && (
                            <span className="ms-1">
                              {sortDirection === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
                            </span>
                          )}
                        </div>
                      </th>
                      <th
                        style={{ width: '15%', cursor: 'pointer' }}
                        onClick={() => onSort('price')}
                        className={sortField === 'price' ? 'table-active' : ''}
                      >
                        <div className="d-flex align-items-center">
                          Price
                          {sortField === 'price' && (
                            <span className="ms-1">
                              {sortDirection === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
                            </span>
                          )}
                        </div>
                      </th>
                      <th
                        style={{ width: '15%', cursor: 'pointer' }}
                        onClick={() => onSort('created')}
                        className={sortField === 'created' ? 'table-active' : ''}
                      >
                        <div className="d-flex align-items-center">
                          Created Date
                          {sortField === 'created' && (
                            <span className="ms-1">
                              {sortDirection === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
                            </span>
                          )}
                        </div>
                      </th>
                      <th style={{ width: '10%' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedProducts.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center py-5">
                          <Package size={48} className="text-muted mb-3" />
                          <h5 className="text-muted" style={{ fontSize: '1rem' }}>No products found</h5>
                          <p className="text-muted" style={{ fontSize: '0.875rem' }}>Start by adding your first product to your store.</p>
                        </td>
                      </tr>
                    ) : (
                      sortedProducts.map(product => (
                        <tr key={product.id}>
                          <td>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selectedProducts.includes(product.id)}
                              onChange={() => handleSelectProduct(product.id)}
                            />
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <img
                                src={product.imageUrl || ""}
                                alt={product.name}
                                className="rounded me-3"
                                style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                              />
                              <div>
                                <div className="fw-semibold">{product.name}</div>
                                <small className="text-muted" style={{ fontSize: '0.75rem' }}>SKU: {product.sku || "N/A"}</small>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="fw-semibold">
                              ${(product.price || 0).toFixed(2)}
                            </span>
                          </td>
                          <td>
                            <span className="text-muted">
                              {product.created ? new Date(product.created).toLocaleDateString() : 'N/A'}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                title="Generate Alt Text"
                                onClick={() => handleGenerateAltText(product.id, product.name)}
                                disabled={generatingAltText[product.id]}
                              >
                                {generatingAltText[product.id] ? (
                                  <span className="loading-dots" role="status" aria-hidden="true"></span>
                                ) : (
                                  <Wand2 size={16} />
                                )}
                              </button>
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                title="Edit Product"
                                onClick={() => handleEditProduct(product.id)}
                              >
                                <Edit size={16} />
                              </button>

                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination and Items Per Page */}
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="d-flex align-items-center">
                  <label className="me-2" style={{ fontSize: '0.875rem' }}>Items per page:</label>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: 'auto' }}
                    value={itemsPerPage}
                    onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
                  >
                    {itemsPerPageOptions.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <nav>
                  <ul className="pagination mb-0">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                    </li>
                    <li className="page-item">
                      <span className="page-link">
                        Page {currentPage} of {Math.ceil(totalProducts / itemsPerPage)}
                      </span>
                    </li>
                    <li className={`page-item ${currentPage >= Math.ceil(totalProducts / itemsPerPage) ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(prev => prev + 1)}
                        disabled={currentPage >= Math.ceil(totalProducts / itemsPerPage)}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products; 